// JS mirror of backend services/npd_calc.py compute() for live UI recalculation.
// New model: E line = Overheads & Margin (combined). SP == GT. Margin% = E/GT.

// Round 51 — Mirror backend R43 substring match: any process whose name
// CONTAINS "shearing" or "slitting" bills on gross weight. Previously was
// strict equality `name === "shearing"` which silently used net weight for
// the very common "Shearing/Slitting" row, producing screen-vs-Excel drift.
const _GROSS_WEIGHT_PROCESS_TOKENS = ["shearing", "slitting"];
const _isGrossWeightProcess = (name) => {
  const lc = (name || "").trim().toLowerCase();
  return _GROSS_WEIGHT_PROCESS_TOKENS.some((tok) => lc.includes(tok));
};

export function computeProcessRowCost(p, n) {
  // MHR (internal): bill by time — shown only as a plain ₹/pc cost in preview/Excel.
  if (p?.use_mhr) return (Number(p?.rate) || 0) * (Number(p?.cycle_time_sec) || 0) / 60;
  const mode = p?.cost_mode || "per_piece";
  if (mode !== "per_kg") return Number(p?.cost) || 0;
  const weight = _isGrossWeightProcess(p?.name)
    ? ((Number(n?.gross_weight) || 0) / Math.max(1, Number(n?.cavity_count) || 1))
    : (Number(n?.net_weight) || 0);
  return (Number(p?.rate) || 0) * weight;
}

function computeTreatment(t, netWeightKg) {
  if (!t) return 0;
  if (typeof t === "number") return Number(t) || 0;
  const mode = t.mode || "per_piece";
  const rate = Number(t.rate) || 0;
  if (mode === "per_piece") return rate;
  if (mode === "per_kg") return rate * (Number(netWeightKg) || 0);
  if (mode === "per_sqin") return rate * (Number(t.surface_area_sqin) || 0);
  return 0;
}

export function getToolMaintenancePct(n) {
  // Returns percentage form (10 = 10%). Legacy decimal (0.10) is auto-upgraded.
  let raw = n.tool_maintenance_pct;
  if (raw === undefined || raw === null) return 10;
  raw = Number(raw);
  if (raw < 1.5) return raw * 100;
  return raw;
}

export function computeNpd(n) {
  // Multiple cavity — effective gross weight = entered gross weight / cavity_count.
  // Single input-point division; formulas below are unchanged. Net weight is NOT divided.
  const _cavities = Math.max(1, Number(n.cavity_count) || 1);
  const gross_weight = (Number(n.gross_weight) || 0) / _cavities;
  const net_weight = Number(n.net_weight) || 0;
  const rm_rate = Number(n.rm_price_per_kg) || 0;
  const scrap_rate = Number(n.scrap_price_per_kg) || 0;
  const scrap_factor = Number(n.scrap_recovery_factor) || 0.9;

  const gross_rmc = gross_weight * rm_rate;
  const scrap_recovery = Math.max(gross_weight - net_weight, 0) * scrap_rate * scrap_factor;
  const net_rmc = gross_rmc - scrap_recovery;

  // Round 51 — Substring match, mirrors backend R43.
  // Shearing OR Slitting → rate × gross; everything else per_kg → rate × net.
  const _procWeight = (p) => {
    return _isGrossWeightProcess(p?.name)
      ? gross_weight
      : net_weight;
  };
  // MHR (CNC/VMC/sliding-head): time-based — ₹/pc = rate(₹/min) × cycle_time_sec ÷ 60.
  const _procAmount = (p) => {
    if (p.use_mhr) return (Number(p.rate) || 0) * (Number(p.cycle_time_sec) || 0) / 60;
    const mode = p.cost_mode || "per_piece";
    return mode === "per_kg" ? (Number(p.rate) || 0) * _procWeight(p) : (Number(p.cost) || 0);
  };
  const process_cost = (n.processes || []).reduce((s, p) => s + _procAmount(p), 0);
  // Per-process computed amounts (for the project-page part breakdown dropdown).
  const processes_calculated = (n.processes || []).map((p) => {
    const mode = p.use_mhr ? "mhr" : (p.cost_mode || "per_piece");
    const amount = _procAmount(p);
    return {
      name: p.name || "",
      mode,
      rate: Number(p.rate) || 0,
      cost: Number(p.cost) || 0,
      tonnage: p.tonnage || "",
      cycle_time_sec: Number(p.cycle_time_sec) || 0,
      weight_basis: mode === "per_kg" ? (_isGrossWeightProcess(p?.name) ? "gross" : "net") : null,
      amount: Math.round(amount * 10000) / 10000,
    };
  });
  const bop_cost = (n.bop_items || []).reduce(
    (s, b) => s + (Number(b.qty) || 0) * (Number(b.unit_price) || 0), 0,
  );

  // Treatment: support new structured input AND legacy *_cost float
  const surfInput = n.surface_treatment || (n.surface_treatment_cost !== undefined
    ? { mode: "per_piece", rate: n.surface_treatment_cost, notes: n.surface_treatment_notes || "" }
    : null);
  const heatInput = n.heat_treatment || (n.heat_treatment_cost !== undefined
    ? { mode: "per_piece", rate: n.heat_treatment_cost, notes: n.heat_treatment_notes || "" }
    : null);
  const surface_cost = computeTreatment(surfInput, net_weight);
  const heat_cost = computeTreatment(heatInput, net_weight);

  const tm_pct = getToolMaintenancePct(n);
  // Prototype cases (Prototype Costing + Proto Project parts) are one-off runs — no tool
  // maintenance loading. Mirrors the backend (services/npd_calc.py).
  const tool_maintenance = n.is_prototype
    ? 0
    : (n.tool_maintenance_overridden
        ? Number(n.tool_maintenance_fixed) || 0
        : process_cost * tm_pct / 100);
  const treatment_total = surface_cost + heat_cost + tool_maintenance;

  // Section F — Tool Amortization (optional, default OFF)
  // R43 — Use deriveTooling helper so R32 override (amortize_over_cycles) is honored.
  // Previously this used a stale inline `cost/life` calc; that diverged from the
  // backend whenever amortize_over_cycles was set and broke Excel-vs-screen parity.
  const amortize_tooling = !!n.amortize_tooling;
  const tooling_cost_one_time = Number(n.tooling_cost) || 0;
  const tooling_life_cycles = Number(n.tooling_life) || 0;
  const _toolDerived = deriveTooling(n);
  const tool_amortization = _toolDerived.applied_per_piece;
  const tool_amortization_full_life = _toolDerived.full_life_per_piece;
  const amortize_basis_cycles = _toolDerived.basis_cycles;
  const _overrideRaw = n?.amortize_over_cycles;
  const amortize_over_cycles =
    _overrideRaw != null && _overrideRaw !== "" ? Number(_overrideRaw) : null;

  const sub_total = net_rmc + process_cost + bop_cost + treatment_total + tool_amortization;

  const ctx = { process_cost, gross_rmc, net_rmc, sub_total };
  let overheads_calculated = (n.overheads || []).map((row) => {
    const v = Number(row.value) || 0;
    const calc = row.basis === "lump_sum" ? v : v * (ctx[row.basis] || 0);
    return { ...row, calculated: Math.round(calc * 10000) / 10000 };
  });
  let overheads_and_margin = overheads_calculated.reduce((s, r) => s + r.calculated, 0);

  // R43 — Mirror backend R32 "Include Carrying Cost in Price" auto-injection.
  // Same conditions: toggle on, MOQ block produces a per-piece carrying cost,
  // and no existing row already covers inventory/carrying/holding/working capital.
  let carrying_double_count_warning = false;
  const includeCarrying = !!n.include_carrying_in_price;
  if (includeCarrying) {
    const moqDerived = deriveMonthlyRmBlock(n, null);
    const moqBlock = moqDerived?.moq_block;
    const perPieceCarry = Number(moqBlock?.per_piece_carrying_cost || 0);
    if (perPieceCarry > 0) {
      const INV_KW = ["inventory", "carrying", "holding", "working capital"];
      const alreadyHas = overheads_calculated.some((r) => {
        const lbl = (r.label || "").toLowerCase();
        return INV_KW.some((kw) => lbl.includes(kw));
      });
      if (alreadyHas) {
        carrying_double_count_warning = true;
      } else {
        const avgInv = moqBlock?.avg_inventory_kg ?? 0;
        const rmRate = Number(n?.rm_price_per_kg || 0);
        const holdingPct = Number(n?.inventory_holding_pct || 0);
        const peakAnnual = moqDerived?.peak_annual_volume_pcs ?? 0;
        overheads_calculated = [
          ...overheads_calculated,
          {
            label: "Inventory Carrying Cost (MOQ-driven)",
            value: Math.round(perPieceCarry * 10000) / 10000,
            basis: "lump_sum",
            is_default: false,
            is_auto: true,
            calculated: Math.round(perPieceCarry * 10000) / 10000,
            remarks: `Avg inventory ${avgInv.toFixed(0)} kg × ₹${rmRate.toFixed(2)}/kg × ${holdingPct.toFixed(1)}% / ${peakAnnual.toFixed(0)} pcs/yr peak demand`,
          },
        ];
        overheads_and_margin += perPieceCarry;
      }
    }
  }

  const grand_total_pre_adj = sub_total + overheads_and_margin;
  let grand_total = grand_total_pre_adj;
  let selling_price = grand_total;            // SP == GT (combined model)

  // R46 — Section F: Custom Adjustments (markup / discount).
  // Applied after E. All rows use the same base; they don't compound.
  const custom_adjustments_in = Array.isArray(n.custom_adjustments) ? n.custom_adjustments : [];
  const custom_adjustments_calculated = [];
  let custom_adjustments_total = 0;
  for (const row of custom_adjustments_in) {
    const mode = (row.mode || "pct").toLowerCase();
    const value = Number(row.value) || 0;
    const amount = mode === "pct"
      ? grand_total_pre_adj * (value / 100.0)
      : value;
    custom_adjustments_calculated.push({
      label: row.label || "",
      mode,
      value,
      amount: Math.round(amount * 10000) / 10000,
    });
    custom_adjustments_total += amount;
  }
  if (custom_adjustments_in.length > 0) {
    grand_total = grand_total_pre_adj + custom_adjustments_total;
    selling_price = grand_total;
  }
  // Section F folds into margin: margin = Section E + Section F, as a % of the final Grand Total.
  const margin_amount = overheads_and_margin + custom_adjustments_total;
  const margin_pct = grand_total > 0 ? (margin_amount / grand_total) * 100 : 0;

  // Round 12 — YoY schedule (Round 17 cap)
  const yoy_schedule = computeYoySchedule(
    grand_total,
    n.yoy_reduction_pct,
    n.project_life_years,
    n.volumes,
    n.yoy_apply_years,
    n.yoy_per_year_pct,
  );
  const total_project_value = Math.round(yoy_schedule.reduce((s, r) => s + r.annual_value, 0) * 100) / 100;

  // Other one-time development costs — one-time only, NOT in per-pc Grand Total.
  const other_dev_costs_in = Array.isArray(n.other_dev_costs) ? n.other_dev_costs : [];
  const other_dev_costs = [];
  let other_dev_total = 0;
  for (const row of other_dev_costs_in) {
    const amt = Number(row.amount) || 0;
    other_dev_costs.push({
      category: row.category || "Other",
      amount: Math.round(amt * 100) / 100,
      remarks: row.remarks || "",
    });
    other_dev_total += amt;
  }
  const total_development_cost = tooling_cost_one_time + other_dev_total;

  return {
    // Canonical RMC keys — stable public contract for ALL displays
    // (LiveCalcPanel, inline RMC cards, NpdQuoteSheet, PDF, Excel, snapshots).
    // Never remove or rename these; add aliases alongside if refactoring.
    gross_rmc: Number.isFinite(gross_rmc) ? gross_rmc : 0,
    scrap_recovery: Number.isFinite(scrap_recovery) ? scrap_recovery : 0,
    net_rmc: Number.isFinite(net_rmc) ? net_rmc : 0,
    process_cost_total: process_cost, processes_calculated, bop_cost_total: bop_cost,
    surface_cost, heat_cost,
    tool_maintenance, treatment_total,
    tool_amortization, tool_amortization_full_life,  // R43 — both for side-by-side
    amortize_tooling, amortize_basis_cycles, amortize_over_cycles,  // R43
    tooling_cost_one_time, tooling_life_cycles,
    // Other one-time development costs (one-time only — NOT in Grand Total)
    other_dev_costs,
    other_dev_total: Math.round(other_dev_total * 100) / 100,
    total_development_cost: Math.round(total_development_cost * 100) / 100,
    sub_total, overheads_calculated,
    overheads_and_margin, total_overheads: overheads_and_margin,
    carrying_double_count_warning,  // R43 — UI surfaces the warning
    // R46 — Section F outputs
    grand_total_before_adj: Math.round(grand_total_pre_adj * 10000) / 10000,
    custom_adjustments_calculated,
    custom_adjustments_total: Math.round(custom_adjustments_total * 10000) / 10000,
    grand_total, selling_price, margin_amount, margin: margin_amount, margin_pct,
    yoy_schedule, total_project_value,
    yoy_reduction_pct: Number(n.yoy_reduction_pct) || 0,
    project_life_years: Number(n.project_life_years) || 1,
    yoy_apply_years: n.yoy_apply_years ?? null,
  };
}

export function computeYoySchedule(grand_total, yoyPct, years, volumes, applyYears, perYearPct) {
  if (!grand_total || grand_total <= 0 || !years || years <= 0) return [];
  const rate = Number(yoyPct || 0) / 100;
  let cap = null;
  const ay = applyYears === undefined || applyYears === null || applyYears === "" ? null : Number(applyYears);
  if (ay !== null && !Number.isNaN(ay) && ay > 0) cap = Math.floor(ay);
  const perYear = perYearPct || {};
  // R54 — reduction for year n: the manual box wins; else the global rate inside the cap
  // window (0 beyond it). With no per-year boxes this reproduces the old compound schedule.
  const rateFor = (n) => {
    if (n < 2) return 0;
    const v = perYear[String(n)];
    if (v !== undefined && v !== null && v !== "") {
      const r = Number(v);
      if (!Number.isNaN(r)) return r / 100;
    }
    return cap === null || n <= cap + 1 ? rate : 0;
  };
  const byYear = {};
  (volumes || []).forEach((v) => { byYear[Number(v.year_number)] = v; });
  const out = [];
  let pricePc = Number(grand_total);
  for (let n = 1; n <= Number(years); n++) {
    const rN = n >= 2 ? rateFor(n) : 0;
    if (n >= 2) pricePc = pricePc * (1 - rN);
    const vol = byYear[n] || {};
    const annual = Number(vol.annual_volume_pcs) || 0;
    const monthly = Number(vol.monthly_volume_pcs) || (annual ? annual / 12 : 0);
    const isCapped = cap !== null && n > cap + 1 && perYear[String(n)] === undefined;
    out.push({
      year_number: n,
      year_label: vol.year_label || "",
      reduction_pct: Math.round(rN * 10000) / 100,
      price_per_pc: Math.round(pricePc * 10000) / 10000,
      annual_volume_pcs: annual,
      monthly_volume_pcs: Math.round(monthly * 10000) / 10000,
      annual_value: Math.round(pricePc * annual * 100) / 100,
      yoy_capped: isCapped,
    });
  }
  return out;
}

const _round4 = (n) => Math.round(n * 10000) / 10000;

export function computeAutoGrossWeight(npd, density) {
  if (!density) return 0;
  const form = (npd && npd.stock_form) || "sheet_strip";

  if (form === "sheet_strip") {
    // R44 — Two gross-weight methods for sheet_strip:
    //   "sheet" → Sheet method (default): sheet dims + utilization, edge-trim waste.
    //   "coil"  → Coil/Progressive: strip_width × pitch × thickness × density.
    const method = (npd.gross_weight_method || "sheet").toLowerCase();
    const strip_w = Number(npd.strip_width) || 0;
    const pitch_v = Number(npd.pitch) || 0;
    const t = Number(npd.thickness_mm) || 0;
    if (!strip_w || !pitch_v || !t) return 0;

    if (method === "coil") {
      return _round4((strip_w * pitch_v * t * density) / 1_000_000);
    }

    // Default = sheet method
    const sw = Number(npd.sheet_width_mm) || 1250;
    const sl = Number(npd.sheet_length_mm) || 2500;
    if (!sw || !sl) return 0;
    const strips_per_sheet = Math.floor(sl / strip_w);
    const pcs_per_strip = Math.floor(sw / pitch_v);
    const pcs_per_sheet = strips_per_sheet * pcs_per_strip;
    if (pcs_per_sheet <= 0) return 0;
    const sheet_weight = (sw * sl * t * density) / 1_000_000;
    return _round4(sheet_weight / pcs_per_sheet);
  }
  if (form === "round_rod") {
    // Costing-sheet method: the FULL bar's weight is spread over the usable length
    // (bar length − end-loss), so each part carries its share of the wasted rod end.
    //   Wt of full bar = π/4 · d² · bar_length · ρ
    //   Parts per bar  = (bar_length − end_loss) / pitch   (1 dp, as the sheet shows)
    //   Gross / part   = Wt of full bar / Parts per bar
    const d = Number(npd.rod_diameter_mm) || 0;
    const pitch = Number(npd.rod_length_mm) || 0;   // per-part length (pitch of feed)
    if (!d || !pitch) return 0;
    const barLen = Number(npd.rod_stock_length_mm) || 3048;   // 10 ft standard bar
    const el = npd.rod_end_loss_mm;
    const endLoss = (el === "" || el == null) ? 248 : (Number(el) || 0);
    const usable = barLen - endLoss;
    const wtFull = (Math.PI * d * d * barLen * density) / 4_000_000;
    if (usable <= 0) return _round4((Math.PI * d * d * pitch * density) / 4_000_000);
    const nos = Math.round((usable / pitch) * 10) / 10;   // parts per bar, 1 dp like the sheet
    if (nos <= 0) return 0;
    return _round4(wtFull / nos);
  }
  if (form === "pipe") {
    const od = Number(npd.pipe_od_mm) || 0;
    const id = Number(npd.pipe_id_mm) || 0;
    const L = Number(npd.pipe_length_mm) || 0;
    if (!od || !L || id >= od) return 0;
    return _round4((Math.PI * (od * od - id * id) * L * density) / 4_000_000);
  }
  if (form === "square_bar") {
    const s = Number(npd.bar_side_mm) || 0;
    const L = Number(npd.bar_length_mm) || 0;
    if (!s || !L) return 0;
    return _round4((s * s * L * density) / 1_000_000);
  }
  if (form === "hex_bar") {
    const af = Number(npd.hex_across_flats_mm) || 0;
    const L = Number(npd.hex_length_mm) || 0;
    if (!af || !L) return 0;
    return _round4(((Math.sqrt(3) / 2) * af * af * L * density) / 1_000_000);
  }
  return 0;
}

// Deprecated wrapper — kept for backwards compatibility with old callers.
// Uses default 1250x2500 sheet for the sheet-method formula.
export function autoGrossWeight(strip_width, pitch, thickness, density) {
  return computeAutoGrossWeight(
    {
      stock_form: "sheet_strip",
      strip_width,
      pitch,
      thickness_mm: thickness,
      sheet_width_mm: 1250,
      sheet_length_mm: 2500,
    },
    density,
  );
}



// R31 — Peak-month RM requirement + low-volume flag + MOQ derivations.
// Mirrors backend services/npd_calc.compute() block for live UI updates.
export function deriveMonthlyRmBlock(npd, material) {
  const grossWt = (Number(npd?.gross_weight || 0)) / Math.max(1, Number(npd?.cavity_count) || 1);
  const volumes = npd?.volumes || [];
  // Peak-year basis: pick the row with the highest annual_volume_pcs
  const peak = volumes.reduce(
    (best, v) =>
      Number(v.annual_volume_pcs || 0) > Number(best?.annual_volume_pcs || 0) ? v : best,
    null,
  ) || {};
  const peakMonthlyPcs = Number(peak.monthly_volume_pcs || 0);
  const peakAnnualPcs = Number(peak.annual_volume_pcs || 0);
  const peakLabel = peak.year_label || (peak.year_number ? `Year ${peak.year_number}` : null);
  const monthlyKg = grossWt * peakMonthlyPcs;
  const threshold = Number(material?.min_monthly_kg_threshold ?? 2000);
  const lowFlag = peakMonthlyPcs > 0 && grossWt > 0 && monthlyKg < threshold;

  const moqKg = Number(npd?.moq_kg || 0);
  const holdingPct = Number(npd?.inventory_holding_pct || 0);
  let moqBlock = null;
  if (moqKg > 0 && grossWt > 0) {
    const batchPcs = moqKg / grossWt;
    const months = peakMonthlyPcs > 0 ? batchPcs / peakMonthlyPcs : null;
    const status =
      months == null ? "unknown" : months <= 6 ? "healthy" : months <= 12 ? "watch" : "excessive";
    const rmRate = Number(npd?.rm_price_per_kg || 0);
    const avgInvKg = moqKg / 2;
    const annualCarrying = avgInvKg * rmRate * (holdingPct / 100);
    const perPiece = peakAnnualPcs > 0 ? annualCarrying / peakAnnualPcs : null;
    moqBlock = {
      moq_kg: moqKg,
      moq_batch_pcs: Math.round(batchPcs),
      months_of_cover: months,
      status,
      avg_inventory_kg: avgInvKg,
      annual_carrying_cost: annualCarrying,
      per_piece_carrying_cost: perPiece,
      inventory_holding_pct: holdingPct,
      moq_confirmed_with: npd?.moq_confirmed_with || null,
      moq_confirmed_at: npd?.moq_confirmed_at || null,
    };
  }
  return {
    monthly_rm_kg: monthlyKg,
    threshold_kg: threshold,
    low_volume_flag: lowFlag,
    peak_year_label: peakLabel,
    peak_monthly_volume_pcs: peakMonthlyPcs,
    peak_annual_volume_pcs: peakAnnualPcs,
    moq_block: moqBlock,
  };
}



// R32 — Tooling amortization basis (full-life vs override). Mirrors backend
// compute() so the form preview updates as the engineer types.
export function deriveTooling(npd) {
  const cost = Number(npd?.tooling_cost || 0);
  const life = Number(npd?.tooling_life || 0);
  const overrideRaw = npd?.amortize_over_cycles;
  const override = overrideRaw != null && overrideRaw !== "" ? Number(overrideRaw) : null;
  const amortizeOn = !!npd?.amortize_tooling;
  // Hard cap at tool life (matches backend defense-in-depth)
  const basisCycles =
    override && override > 0
      ? life > 0
        ? Math.min(override, life)
        : override
      : life;
  const applied = amortizeOn && basisCycles > 0 ? cost / basisCycles : 0;
  const fullLife = amortizeOn && life > 0 ? cost / life : 0;
  return {
    applied_per_piece: applied,
    full_life_per_piece: fullLife,
    basis_cycles: basisCycles,
    has_override: override != null && life > 0 && override < life,
  };
}
